import React, { useState, useRef, useEffect } from 'react';
import { authService } from 'services/auth.service';
import './ProfileDropDown.css';

const LogoutIcon = () => (
  <svg
    fill="none"
    height="16"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="16"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16,17 21,12 16,7" />
    <line
      x1="21"
      x2="9"
      y1="12"
      y2="12"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    fill="none"
    height="14"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="14"
  >
    <polyline points="6,9 12,15 18,9" />
  </svg>
);

interface IProfileDropDownProps {
  firstName: string;
}

const ProfileDropDown: React.FC<IProfileDropDownProps> = ({ firstName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user initials for avatar
  const getUserInitials = (name: string): string => {
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Handle logout
  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(false);
    authService.logout();
    window.location.replace('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div
      className="profile-dropdown"
      ref={dropdownRef}
    >
      <button
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`Account menu for ${firstName}`}
        className={`profile-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        type="button"
      >
        <div className="profile-avatar no-image">
          {getUserInitials(firstName)}
        </div>
        <div className="profile-info">
          <div className="profile-name">{firstName}</div>
        </div>
        <div className="profile-chevron">
          <ChevronDownIcon />
        </div>
      </button>

      <div
        className={`profile-menu ${isOpen ? 'open' : ''}`}
        role="menu"
      >
        <button
          className="profile-menu-item danger"
          onClick={handleLogout}
          role="menuitem"
          type="button"
        >
          <span className="profile-menu-icon">
            <LogoutIcon />
          </span>
          <span className="profile-menu-text">Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default ProfileDropDown;